/**
 * คีมเล็บพยัคฆ์ไฟ (tongs-claw): the caster's body through the lock routine bc-maul พยัคฆ์กดเหยื่อ (rules' table
 * packages/shared/src/data/longMoves/bloodClaw.ts, keys bc…). Tiger style, ideas only from the southern tiger-claw arts:
 * low and heavy (deep knees, the back rounded over the hands, the head forward), the hands hooked as claws (the rig has no
 * finger shapes: the claws read through the wrists driving down and across), every rake loaded from the shoulder first so
 * the blow has its wind-up. Hand-to-hand (no weapon arm, so no `ARMS`).
 *
 * The foe lies under the caster from the mount to the flurry (`downHit`): the claws work low, at the ground in front.
 * 40 keys over the 5.2 s routine, blended on the spline (motions/spline.ts) like every long move.
 */
import type { Ease, LimbSpec } from '../kit';
import type { Pose } from '../index';
import { buildLong, type LongKey } from './kit';

export const ARMS = {} satisfies Record<string, LimbSpec>;

type H = [number, number];
/** A key: the low tiger stance by default (wide feet, deep knees, back rounded forward), both claws at the given places. */
const K = (A: H, B: H, o: Partial<LongKey> = {}): LongKey => ({
  feet: 1.8, f: [-10, 9], o: 11, tilt: 18, hunch: 2, arm: 'forward', far: 'fwd', face: 'atk', A, B, ...o,
});

/** bcPounce: sink and load on the back leg, spring low and long with both claws up, drive the foe down, settle on it. */
const POUNCE: LongKey[] = [
  K([7, 30], [5, 32], { o: 12, tilt: 26, hunch: 2, f: [-11, 6], w: -0.6, sway: 1 }),
  K([17, 17], [15, 19], { o: 6, tilt: 26, f: [-12, 10], l: [6, 3], lift: 10, rot: 0.15, sway: -2, hem: 2, flare: 2 }),
  K([18, 35], [16, 37], { o: 11, tilt: 32, hunch: 2, f: [-9, 12], l: [1, 0], lift: 2, rot: 0.08, hem: 2, flare: 2 }),
  K([17, 38], [15, 39], { o: 12, tilt: 32, hunch: 2, f: [-9, 12], hem: 2, flare: 1 }),
];
/** bcMount: crouched over the downed foe, both claws pinning it, the shoulders roll (the growl), the right claw lifts. */
const MOUNT: LongKey[] = [
  K([16, 38], [14, 39], { o: 12, tilt: 32, hunch: 2, f: [-9, 11], turn: 1, sway: 0.5 }),
  K([16, 38], [14, 39], { o: 12, tilt: 30, hunch: 2, f: [-9, 11], turn: -1, headDx: 1, sway: -0.5 }),
  K([10, 26], [15, 38], { o: 12, tilt: 28, hunch: 2, f: [-9, 11], turn: 1 }),
];
/** bcRakeL / bcRakeR: one claw loads high by the ear (shoulder back), then rakes down and across the foe, the other pins. */
const RAKE = (s: 1 | -1): LongKey[] => {
  const load: H = [2, 17];
  const mid: H = [12, 26];
  const hit: H = [19, 39];
  const thru: H = [10, 41];
  const pin: H = [15, 39];
  const k = (hand: H, o: Partial<LongKey>) => (s > 0 ? K(pin, hand, o) : K(hand, pin, o));
  return [
    k(load, { o: 11, tilt: 24, hunch: 2, f: [-9, 11], turn: 2 * s, sway: s }),
    k(mid, { o: 12, tilt: 29, hunch: 2, f: [-9, 11], turn: 0, sway: 0 }),
    k(hit, { o: 12, tilt: 34, hunch: 2, f: [-9, 11], turn: -2 * s, sway: -s, hem: 2 }),
    k(thru, { o: 12, tilt: 32, hunch: 2, f: [-9, 11], turn: -s, hem: 1 }),
  ];
};
/** bcRakeFlurry: four short rakes, claws alternating, each loaded only to the chest (the speed), the body pumping. */
const FLURRY: LongKey[] = [
  K([16, 39], [6, 24], { o: 12, tilt: 28, hunch: 2, f: [-9, 11], turn: 1 }),
  K([7, 24], [18, 40], { o: 12, tilt: 33, hunch: 2, f: [-9, 11], turn: -1, sway: -1 }),
  K([18, 40], [7, 25], { o: 12, tilt: 33, hunch: 2, f: [-9, 11], turn: 1, sway: 1 }),
  K([7, 24], [18, 40], { o: 12, tilt: 33, hunch: 2, f: [-9, 11], turn: -1, sway: -1 }),
  K([18, 40], [7, 25], { o: 12, tilt: 34, hunch: 2, f: [-9, 11], turn: 1, sway: 1, hem: 2 }),
  K([9, 26], [17, 39], { o: 12, tilt: 32, hunch: 2, f: [-9, 11], turn: -1 }),
  K([17, 39], [9, 27], { o: 12, tilt: 33, hunch: 2, f: [-9, 11], turn: 1, hem: 2 }),
  K([15, 38], [13, 39], { o: 12, tilt: 31, hunch: 2, f: [-9, 11] }),
];
/** bcFlip: both claws hook under the foe, then haul it over to the side and up, the body rising behind the pull. */
const FLIP: LongKey[] = [
  K([17, 41], [15, 41], { o: 12, tilt: 34, hunch: 2, f: [-9, 11] }),
  K([14, 34], [12, 36], { o: 12, tilt: 26, hunch: 1, f: [-10, 10], turn: 2, w: -0.5, sway: 1 }),
  K([6, 26], [3, 28], { o: 12, tilt: 10, f: [-10, 9], turn: 3, w: -0.7, sway: 2, hem: 2, flare: 2 }),
  K([8, 27], [6, 29], { o: 12, tilt: 10, f: [-10, 9], turn: 1, w: -0.3, sway: 1, hem: 1 }),
];
/** bcRoar: claws drawn in to the chest, then the chest thrown open, claws wide and high, head back, held (shaking). */
const ROAR: LongKey[] = [
  K([8, 26], [6, 27], { o: 11, tilt: 12, hunch: 2, f: [-10, 9] }),
  K([12, 13], [-6, 15], { o: 9, tilt: -8, hunch: 0, f: [-10, 9], headDx: -1, sway: -1, hem: 2, flare: 2, face: 'cast' }),
  K([13, 12], [-7, 14], { o: 9, tilt: -9, hunch: 0, f: [-10, 9], headDx: -1, sway: 1, hem: 2, flare: 2, face: 'cast' }),
  K([12, 14], [-6, 16], { o: 10, tilt: -6, hunch: 0, f: [-10, 9], sway: -0.5, hem: 2, flare: 2, face: 'cast' }),
];
/** bcRendUp: drop deep, both claws down by the knees (the wind-up), then tear up through the foe to over the head. */
const REND: LongKey[] = [
  K([11, 41], [9, 42], { o: 12, tilt: 30, hunch: 2, f: [-10, 10], w: -0.4 }),
  K([17, 24], [15, 26], { o: 8, tilt: 6, hunch: 0, f: [-10, 11], w: 0.4, hem: 2, flare: 2 }),
  K([15, 9], [13, 11], { o: 4, tilt: -8, hunch: 0, f: [-10, 11], l: [0, 2], w: 0.6, lift: 4, rot: -0.08, hem: 2, flare: 2, sway: -2 }),
  K([14, 11], [12, 13], { o: 5, tilt: -6, hunch: 0, f: [-10, 11], w: 0.5, lift: 1, hem: 2, flare: 2, sway: -1 }),
];
/** bcCrouch: drop back into the low tiger crouch as the foe falls, claws in front. */
const CROUCH: LongKey[] = [
  K([12, 30], [9, 32], { o: 11, tilt: 22, hunch: 2, f: [-10, 9], sway: 1, hem: 2 }),
  K([13, 29], [9, 31], { o: 11, tilt: 15, hunch: 2, f: [-10, 9] }),
];
/** bcStance: rise to the tiger guard (lead claw high, rear claw low), then back to ready. */
const STANCE: LongKey[] = [
  K([14, 22], [8, 30], { o: 10, tilt: 8, hunch: 1, f: [-9, 8] }),
  K([12, 24], [8, 30], { o: 7, tilt: 4, hunch: 0, feet: 1.3, f: [-7, 6], face: 'idle' }),
];

export type BloodClawMotionId =
  | 'bcPounce' | 'bcMount' | 'bcRakeL' | 'bcRakeR' | 'bcRakeFlurry'
  | 'bcFlip' | 'bcRoar' | 'bcRendUp' | 'bcCrouch' | 'bcStance';
type N = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;
export type Poses = `${BloodClawMotionId}_${N}`;

const built = buildLong<BloodClawMotionId, Poses>('คีมเล็บพยัคฆ์ไฟ', {
  bcPounce: { keys: POUNCE, from: 'ready', ease: ['inOut', 'in', 'in', 'out'] },
  bcMount: { keys: MOUNT, from: 'bcPounce_4' },
  bcRakeL: { keys: RAKE(1), from: 'bcMount_3', ease: ['out', 'in', 'in', 'out'] },
  bcRakeR: { keys: RAKE(-1), from: 'bcRakeL_4', ease: ['out', 'in', 'in', 'out'] },
  bcRakeFlurry: { keys: FLURRY, from: 'bcRakeR_4', ease: Array(8).fill('inOut') as Ease[] },
  bcFlip: { keys: FLIP, from: 'bcRakeFlurry_8', ease: ['inOut', 'in', 'out', 'inOut'] },
  bcRoar: { keys: ROAR, from: 'bcFlip_4', ease: ['inOut', 'out', 'inOut', 'inOut'] },
  bcRendUp: { keys: REND, from: 'bcRoar_4', ease: ['inOut', 'in', 'out', 'inOut'] },
  bcCrouch: { keys: CROUCH, from: 'bcRendUp_4', ease: ['in', 'out'] },
  bcStance: { keys: STANCE, from: 'bcCrouch_2', to: 'ready' },
});

export const POSES = built.POSES;
export const MOTIONS = built.MOTIONS as unknown as readonly { id: BloodClawMotionId; keys: readonly Pose[]; ease: readonly Ease[] }[];
export const CHANNELS = built.CHANNELS;
